import { useAccountContext, useCustomerContext } from '@ovotech/ui-tools';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import SiteLoading from '@/src/components/Wrapper/SiteLoading';
import { BETA_WEBSITE_URL } from '@/src/constants/endpoints';
import { NEW_WEB_REDIRECT } from '@/src/constants/feature-flags';
import { trackUserAction } from '@/src/services/datadog';
import { useDecision } from '@/src/utils/optimizely';
import { useNewWebEligibility } from '@/src/utils/newWebEligibility/useNewWebEligibility';
import { setWebPreference } from '@/src/utils/webPreference';

// Only the flagged cohort ever waits.
const ELIGIBILITY_WAIT_MS = 2000;

type Outcome = 'redirect' | 'stay';

const verdict = (eligible: boolean): Outcome =>
  eligible ? 'redirect' : 'stay';

const NewWebRedirect = ({
  customerId,
  children,
}: PropsWithChildren<{ customerId: string }>) => {
  const { accountId } = useAccountContext();
  const { isEligible, isReady, isEligibleAtTimeout } =
    useNewWebEligibility(accountId);
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  const [settled, setSettled] = useState<Outcome | null>(null);

  // The full verdict — Home Services veto included — gets until the cap;
  // redirecting on the core checks alone would race the veto out.
  let outcome = settled;
  if (!outcome) {
    if (isReady) outcome = verdict(isEligible);
    else if (waitedTooLong) outcome = verdict(isEligibleAtTimeout);
  }

  useEffect(() => {
    const timer = setTimeout(() => setWaitedTooLong(true), ELIGIBILITY_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  // Freeze the verdict: a late query result must not overturn the on-screen page.
  useEffect(() => {
    if (!outcome || settled) return;
    setSettled(outcome);
    trackUserAction('new_web_redirect_decision', {
      outcome,
      decidedBy: isReady ? 'eligibility' : 'timeout',
    });
  }, [outcome, settled, isReady]);

  useEffect(() => {
    if (outcome !== 'redirect') return;
    setWebPreference('new-web', customerId);
    window.location.replace(BETA_WEBSITE_URL);
  }, [outcome, customerId]);

  return outcome === 'stay' ? <>{children}</> : <SiteLoading />;
};

// Flag before eligibility, so only the flagged cohort mounts its queries.
export const NewWebRedirectDecision = ({ children }: PropsWithChildren) => {
  const { customerId } = useCustomerContext();
  const [decision] = useDecision(NEW_WEB_REDIRECT);

  if (!decision.enabled || !customerId) return <>{children}</>;

  return <NewWebRedirect customerId={customerId}>{children}</NewWebRedirect>;
};
